// 主治醫師高風險及高技術項目設定整頁（customer-tsgh-high-risk-setting＝legacy
// customer/tsgh/highRiskSetting.jsp，/tsghfunc/toHighRiskSetting，display 頁型）。
// 批次 17（B8-3，Gitea issue #16）Wave 2。掛在 PageBehavior.complexBody（批次 13/16 hook 先例；
// DisplayPage 已於批次 17 補 complexBody 早退）。
//
// legacy 流程（行號親驗 2026-09-25，基準＝highRiskSetting.jsp＋TsghCustomerFuncController.java；
// 詳 docs/plans/2026-09-25-batch17-vote-setting-plan.md §1.5/§1.7）：
//   - ready（:16-26）：setOrganizationElement→預設 myOrganization→change→organizationChangeEvent
//     （:28-47，部門清單快取）→預設 myDepartment→change→departmentChangeEvent→doQuery（ready 即查詢）。
//   - initListTable（:53-96）6 欄：col0 **title＝新增 btn-warning onclick=addNewRow()（表頭按鈕）**＋
//     itemCode select（檢查項目 0／手術項目 1）；col1 序號 itemValue3 text；col2 項目名稱 itemDesc1 text；
//     col3 見習例數 itemValue1 number；col4 實作例數 itemValue2 number；col5 刪除 btn-danger。
//   - addNewRow（:99-109）：{id:newRowId(負數遞減), itemValue3:'0', itemCode:'0', itemDesc1:'',
//     itemValue1:'0', itemValue2:'0'}。
//   - doQuery（:112-146）：deptId 空/''/<0→`請選擇醫院及部門`；POST tsghfunc/getHighRiskDeptSettingListAjax
//     {deptId}→排序 itemCode asc→itemValue3 asc→rows.add。
//   - doSave（:149-176）：逐列組 dataList（settingType '2'/itemCode/itemValue3/isEnable '1'/erasable '1'/
//     sort '0'/departmentId/itemDesc1/itemValue1/itemValue2）→POST tsghfunc/saveHighRiskDeptSettingAjax
//     （**param 變數組了沒用＝quirk 照實**）→成功 info(desc)＋doQuery；失敗 alert(desc)。
//   - deleteItem（:178-193）：POST tsghfunc/deleteTsghSettingAjax {id}；controller :544-548 id>0→
//     `刪除資料成功!`，否則 `刪除資料失敗!`。
//   - 存檔 gate serviceStandardSetting-UPDATE（:232 **copy-paste 誤用照實**；controller ACCESS＝
//     highRiskSetting-ACCESS :466）。
//
// 雛形策略（執行文件 D 系列）：存檔＝update（endpoint-map update:customer-tsgh-high-risk-setting ✓）＋
// dataEffect（C5-j）＋成功訊息 `更新資料成功!`（controller :530 字面值）；刪除＝remove（✓ mapped）＋
// `刪除資料成功!`（:546；新列 id<=0→照 controller :547-548 alert `刪除資料失敗!` 不送）；查詢＝list
// （✓ mapped；科部 select 觸發＝無按鈕可掛 data-effect，讀取端點 parity 不可見，登記）；級聯種子平列
// （批次 15 D2 先例）＋ready 即查詢；表格輸入不帶 name（契約欄位維度僅 2 select＝raw DOM 擷取時表格空，
// D4）；存檔 payload 補帶 organizationId/departmentId（legacy 只送 deptId；C5-h 接線，登記）＋datas 逐列。
// 種子 id 覆寫為數字字串（controller id>0 分支可測）【雛形值待實機校準】。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { ContentPanel, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import { MockDataError, useDataSource } from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import { ACCOUNT_DEPARTMENT_OPTIONS, ACCOUNT_ORGANIZATION_OPTIONS } from '@/mocks/resource-rules';
import type { Row } from '@/mocks/types';

/** 訊息字面值（highRiskSetting.jsp＋TsghCustomerFuncController.java）。 */
const TEXT_NO_DEPT = '請選擇醫院及部門'; // :114
const TEXT_SAVE_SUCCESS = '更新資料成功!'; // controller :530（!＝0021）
const TEXT_SAVE_ERROR = '更新資料失敗!'; // controller :528/:534
const TEXT_DELETE_SUCCESS = '刪除資料成功!'; // controller :546
const TEXT_DELETE_ERROR = '刪除資料失敗!'; // controller :548/:552
const TEXT_QUERY_ERROR = '查詢資料失敗!'; // controller :508

export function HighRiskSettingBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  // ------------------------------------------------------------------
  // 級聯（:16-51；種子平列，批次 15 D2 先例）：org 變動＝重查（平列池不分院，登記）；
  // dept 變動→doQuery（ready 即查詢）。
  // ------------------------------------------------------------------
  const [organizationId, setOrganizationId] = useState('org-1');
  const [departmentId, setDepartmentId] = useState('dept-1');
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1；
  // override 命中即移除 placeholder 空選項——凍結 departmentId 為 [共用(0),管理部門(1)] 二選項無空首項）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  /** 持久錨點列（resource 第 0 列；設定存列上＝B1-2/批次 16 先例）。 */
  const [anchorId, setAnchorId] = useState('');

  /** doQuery（:112-146）：gate＋list＋排序（itemCode asc→itemValue3 asc）。 */
  const doQuery = () => {
    void (async () => {
      // :113-116 gate（初始 dept='dept-1' 恆通過； legacy 於級聯未載時觸發）。
      if (departmentId === '' || Number.parseInt(departmentId, 10) < 0) {
        await dialogs.alert(TEXT_NO_DEPT);
        return;
      }
      setBusy(true);
      try {
        const result = await dataSource.list(pageId, {
          filters: { departmentId },
        });
        // :125-138 排序（itemCode asc→itemValue3 asc）。
        const sorted = [...result.rows].sort((left, right) => {
          const codeDelta =
            Number.parseInt(String(left['itemCode'] ?? '0'), 10) -
            Number.parseInt(String(right['itemCode'] ?? '0'), 10);
          if (codeDelta !== 0) {
            return codeDelta;
          }
          return (
            Number.parseInt(String(left['itemValue3'] ?? '0'), 10) -
            Number.parseInt(String(right['itemValue3'] ?? '0'), 10)
          );
        });
        setRows(sorted);
      } catch (cause: unknown) {
        setBusy(false);
        setLoading(false);
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_QUERY_ERROR);
      } finally {
        setBusy(false);
        setLoading(false);
      }
    })();
  };

  useEffect(() => {
    void (async () => {
      try {
        // 錨點列（resource 最後一列；種子只覆寫前 3 列，刪種子列不會斷錨；設定存列上＝B1-2 先例）。
        const anchor = await dataSource.list(pageId, { page: 1, pageSize: 12 });
        setAnchorId(String(anchor.rows[anchor.rows.length - 1]?.id ?? ''));
      } catch {
        setAnchorId('');
      }
    })();
    doQuery(); // ready 即查詢（:48-51 departmentChangeEvent 鏡像）
    // pageId 一次掛載內不變（ComplexFormPage 同款註記）。
    // biome-ignore lint/correctness/useExhaustiveDependencies: 見上
  }, [pageId]);

  // ------------------------------------------------------------------
  // 列編輯（initListTable :53-96 的可編輯儲存格；雛形以列狀態綁定）
  // ------------------------------------------------------------------
  const patchRow = (id: string, key: string, value: string) => {
    setRows((current) =>
      current.map((row) => (String(row['id']) === id ? { ...row, [key]: value } : row)),
    );
  };

  /** addNewRow（:99-109）：id=newRowId 負數遞減；itemValue3/itemCode '0'、例數 '0'。 */
  const addNewRow = () => {
    setRows((current) => {
      const minId = current.reduce(
        (min, row) => Math.min(min, Number.parseInt(String(row['id'] ?? '0'), 10) || 0),
        0,
      );
      const newRow: Row = {
        id: String(minId - 1),
        itemValue3: '0',
        itemCode: '0',
        itemDesc1: '',
        itemValue1: '0',
        itemValue2: '0',
        departmentId,
        settingType: '2',
      };
      return [...current, newRow];
    });
  };

  // ------------------------------------------------------------------
  // 存檔（doSave :149-176）
  // ------------------------------------------------------------------
  const doSave = () => {
    void (async () => {
      // 逐列組 dataList（:150-169；legacy 讀 DOM id 尾碼＝雛形讀列狀態）。
      const datas = rows.map((row) => ({
        id: String(row['id'] ?? ''),
        settingType: '2',
        itemCode: String(row['itemCode'] ?? '0'),
        itemValue3: String(row['itemValue3'] ?? ''),
        isEnable: '1',
        erasable: '1',
        sort: '0',
        departmentId,
        itemDesc1: String(row['itemDesc1'] ?? ''),
        itemValue1: String(row['itemValue1'] ?? '0'),
        itemValue2: String(row['itemValue2'] ?? '0'),
      }));
      if (anchorId === '') {
        await dialogs.alert(t('error.generic'));
        return;
      }
      setBusy(true);
      try {
        // legacy 只送 deptId；雛形補帶 organizationId/departmentId（C5-h 接線，D4 登記）。
        await dialogs.processing.wrap(
          dataSource.update(pageId, anchorId, {
            organizationId,
            departmentId,
            deptId: departmentId,
            datas,
          }),
        );
        // 先收遮罩再跳對話框：info() 要等使用者按關閉才 resolve，busy 若留到 finally 才清，
        // 本頁 z-[3000] 的遮罩會整段蓋住資訊對話框（issue #56）。
        setBusy(false);
        await dialogs.info(TEXT_SAVE_SUCCESS); // controller :530
        doQuery(); // :172
      } catch (cause: unknown) {
        setBusy(false);
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_SAVE_ERROR);
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 刪除（deleteItem :178-193）：id>0→remove＋`刪除資料成功!`；新列 id<=0→`刪除資料失敗!` 不送
   * （controller :544-548 分支鏡像）。 */
  const deleteItem = (row: Row) => {
    void (async () => {
      const id = String(row['id'] ?? '');
      const numeric = Number.parseInt(id, 10);
      if (Number.isNaN(numeric) || numeric <= 0) {
        await dialogs.alert(TEXT_DELETE_ERROR);
        return;
      }
      setBusy(true);
      try {
        await dialogs.processing.wrap(dataSource.remove(pageId, id));
        setBusy(false); // 同 doSave：先收遮罩再跳資訊對話框（issue #56）
        await dialogs.info(TEXT_DELETE_SUCCESS);
        doQuery();
      } catch (cause: unknown) {
        setBusy(false);
        await dialogs.alert(cause instanceof MockDataError ? cause.message : TEXT_DELETE_ERROR);
      } finally {
        setBusy(false);
      }
    })();
  };

  const rowKey = (row: Row): string => String(row['id']).toLowerCase();

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      {/* showProcessing 鏡像（doQuery/doSave 期間；busy 由 update/list 流程置位）。 */}
      <ProcessingOverlay uiId={`${pageId}-processing`} open={loading || busy} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="主治醫師高風險及高技術項目設定"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {/* 查詢列（:210-224）：*醫院 organizationId＋*科部 departmentId（級聯種子平列）。 */}
        <div data-ui-id={`${pageId}-query-form`} className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-right text-sm" htmlFor={`${pageId}-query-org`}>
              <span className="text-danger">*</span>醫院：
            </label>
            <Select
              uiId={`${pageId}-query-org`}
              name="organizationId"
              value={organizationId}
              onChange={(next) => {
                setOrganizationId(next);
                doQuery(); // legacy org change→dept 重載→doQuery；平列池直接重查（登記）
              }}
              options={overrideOptions('organizationId') ?? ACCOUNT_ORGANIZATION_OPTIONS}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-right text-sm" htmlFor={`${pageId}-query-dept`}>
              <span className="text-danger">*</span>科部：
            </label>
            <Select
              uiId={`${pageId}-query-dept`}
              name="departmentId"
              value={departmentId}
              onChange={(next) => {
                setDepartmentId(next);
                doQuery(); // :48-51 departmentChangeEvent
              }}
              options={overrideOptions('departmentId') ?? ACCOUNT_DEPARTMENT_OPTIONS}
              // GNL-002（批次 29，issue #30）：首項鏡像 legacy 請選擇(-1)（customer-tsgh-high-risk-setting raw-dom）；
              // placeholder value="" 與 legacy -1 的語意差記錄結案（mock query 不真送 server）。
              // 批次 52 修訂：override 命中（凍結 [共用(0),管理部門(1)]）即取代＋去 placeholder。
              {...(overrideOptions('departmentId') ? {} : { placeholder: '請選擇' })}
            />
          </div>
        </div>

        {/* 可編輯表（:225-229 #formTable；6 欄含表頭新增鈕＝TableColumn header ReactElement 批次 15 先例）。 */}
        <div className="mt-3">
          <Table
            uiId={`${pageId}-form-table`}
            columns={[
              {
                key: 'itemCode',
                // col0 表頭＝新增鈕（:54 title="<button … onclick=addNewRow()>"）。
                header: (
                  <Button
                    uiId={`${pageId}-add-new-row`}
                    variant="default"
                    size="sm"
                    type="button"
                    onClick={addNewRow}
                  >
                    新增
                  </Button>
                ),
                align: 'center',
              },
              { key: 'itemValue3', header: '序號', align: 'center' },
              { key: 'itemDesc1', header: '項目名稱', align: 'center' },
              { key: 'itemValue1', header: '見習例數', align: 'center' },
              { key: 'itemValue2', header: '實作例數', align: 'center' },
              { key: 'action', header: '', align: 'center' },
            ]}
            rows={rows as readonly TableRow[]}
            rowKey={rowKey}
            emptyText="查無符合的資料"
            renderCell={(row, columnKey) => {
              const id = String(row['id']);
              if (columnKey === 'itemCode') {
                // :57-66 itemCode select（檢查項目 0／手術項目 1）。
                return (
                  <Select
                    uiId={`${pageId}-row-${rowKey(row)}-item-code`}
                    value={String(row['itemCode'] ?? '0')}
                    onChange={(next) => {
                      patchRow(id, 'itemCode', next);
                    }}
                    options={[
                      { value: '0', label: '檢查項目' },
                      { value: '1', label: '手術項目' },
                    ]}
                  />
                );
              }
              if (columnKey === 'itemValue3') {
                // :68-76 序號 text（name=itemValue3 legacy 照實有 name；雛形不帶 name＝D4）。
                return (
                  <TextInput
                    uiId={`${pageId}-row-${rowKey(row)}-item-value3`}
                    value={String(row['itemValue3'] ?? '')}
                    onChange={(next) => {
                      patchRow(id, 'itemValue3', next);
                    }}
                  />
                );
              }
              if (columnKey === 'itemDesc1') {
                return (
                  <TextInput
                    uiId={`${pageId}-row-${rowKey(row)}-item-desc1`}
                    value={String(row['itemDesc1'] ?? '')}
                    onChange={(next) => {
                      patchRow(id, 'itemDesc1', next);
                    }}
                  />
                );
              }
              if (columnKey === 'itemValue1' || columnKey === 'itemValue2') {
                // :77-92 見習/實作例數 number。
                const inputKey = columnKey === 'itemValue1' ? 'item-value1' : 'item-value2';
                return (
                  <input
                    data-ui-id={`${pageId}-row-${rowKey(row)}-${inputKey}`}
                    type="number"
                    className="border border-gray-300 px-2 py-1 text-sm"
                    value={String(row[columnKey] ?? '0')}
                    onChange={(event) => {
                      patchRow(id, columnKey, event.target.value);
                    }}
                  />
                );
              }
              if (columnKey === 'action') {
                // :93-95 刪除 btn-danger。
                return (
                  <Button
                    uiId={`${pageId}-row-${rowKey(row)}-delete`}
                    variant="danger"
                    size="sm"
                    type="button"
                    // C5-j：刪除真的呼叫 dataSource.remove（id>0 分支）。
                    dataEffect={`remove:${pageId}`}
                    onClick={() => {
                      deleteItem(row);
                    }}
                  >
                    刪除
                  </Button>
                );
              }
              return '\u00a0';
            }}
          />
        </div>

        {/* 存檔（:231-235 btn-primary onclick=doSave()；gate 誤用字串照實不影響，D5）。 */}
        <div className="mt-3">
          <Button
            uiId={`${pageId}-actions-save`}
            variant="primary"
            size="sm"
            type="button"
            // C5-j：存檔真的呼叫 dataSource.update（endpoint-map update:… ✓）。
            // 不 disabled（transcript 先例：legacy 以 overlay 擋點擊＝測試 race 安全）。
            dataEffect={`update:${pageId}`}
            onClick={doSave}
          >
            存檔
          </Button>
        </div>
      </ContentPanel>
    </div>
  );
}
